import { Injectable } from "@angular/core";

import * as luxon from 'luxon';

const DateTime = luxon.DateTime;

@Injectable({ providedIn: 'root' })
export class DateTimeUtilService {

    calcDurationMinutes(startUtcDateTime: string, endUtcDateTime: string, interval?: number, minValue?: number, maxValue?: number) {

        const startDateTime = this.parseDateTime(startUtcDateTime);
        const endDateTime = this.parseDateTime(endUtcDateTime);

        const duration = endDateTime.diff(startDateTime, 'minutes');

        if (interval) {
            return duration.minutes - (duration.minutes % interval);
        }

        if (minValue && interval < minValue) {
            return minValue;
        }

        if (maxValue && interval > maxValue) {
            return maxValue;
        }

        return duration?.minutes;
    }

    isWithinDateRange(start: string, end: string, start1: string, end1: string): boolean {
        const startDateTime = this.parseDateTime(start);
        const endDateTime = this.parseDateTime(end);
        const startDateTime1 = this.parseDateTime(start1);
        const endDateTime1 = this.parseDateTime(end1);

        if (startDateTime1 < endDateTime && endDateTime1 > startDateTime) {
            return true;
        }

        return false;
    }

    calcDurationSeconds(startUtcDateTime: string, endUtcDateTime: string, interval?: number, minValue?: number, maxValue?: number) {

        const startDateTime = this.parseDateTime(startUtcDateTime);
        const endDateTime = this.parseDateTime(endUtcDateTime);

        const duration = endDateTime.diff(startDateTime, 'seconds');

        if (interval) {
            return duration.minutes - (duration.minutes % interval);
        }

        if (minValue && interval < minValue) {
            return minValue;
        }

        if (maxValue && interval > maxValue) {
            return maxValue;
        }

        return duration?.seconds;
    }


    DATE_TIME_FORMAT = 'MM/dd/yyyy hh:mm a';

    readonly minutesConst = 60;
    readonly hoursConst = 60 * 60;
    readonly daysConst = this.hoursConst * 24;

    private parseDateTime(dateTime: string, options?: luxon.DateTimeOptions): luxon.DateTime {
        if (!dateTime) {
            console.error('DateTimeUtilService.parseDateTime: dateTime is null or empty');
            return {} as luxon.DateTime;
        }

        let d = DateTime.fromISO(dateTime, options);

        if (!d.isValid) {
            d = DateTime.fromFormat(dateTime, this.DATE_TIME_FORMAT, options);
        }

        return d;
    }

    formatDateTimeToTimezone(dateTime: string, timezone: string, format?: string): string {
        if (!dateTime) {
            return '';
        }

        format = format || this.DATE_TIME_FORMAT;

        const opt: luxon.DateTimeOptions = {};

        opt.setZone = true;
        opt.zone = 'UTC';

        const luxonDateTime = this.parseDateTime(dateTime, opt);

        return luxonDateTime.setZone(timezone).toFormat(format);
    }

    formatDateTime(dateTime: string, format?: string): string {
        if (!dateTime) {
            return '';
        }

        format = format || this.DATE_TIME_FORMAT;

        const opt: luxon.DateTimeOptions = {};

        opt.setZone = true;
        opt.zone = 'UTC';

        const luxonDateTime = this.parseDateTime(dateTime, opt);

        return luxonDateTime.toFormat(format);
    }

    isToday(dateTime: string | Date): boolean {
        if (!dateTime) {
            return false;
        }

        if (dateTime instanceof Date) {
            return this.parseDateTime(dateTime.toISOString()).hasSame(DateTime.local(), 'day');
        }

        return this.parseDateTime(dateTime).hasSame(DateTime.local(), 'day');
    }

    isBefore(currentStart: string, crossStart: string) {
        if (!currentStart || !crossStart) {
            return false;
        }

        const timeDiff = this.parseDateTime(currentStart).diff(this.parseDateTime(crossStart));

        if (timeDiff.milliseconds < 0) {
            return true;
        }

        return false;
    }

    isAfter(currentEnd: string, crossEnd: string) {
        if (!currentEnd || !crossEnd) {
            return false;
        }
        const timeDiff = this.parseDateTime(currentEnd).diff(this.parseDateTime(crossEnd));

        if (timeDiff.milliseconds > 0) {
            return true;
        }

        return false;
    }

    isBeforeToday(dateTime: string): boolean {
        if (!dateTime) {
            return false;
        }

        const timeDiff = this.parseDateTime(dateTime).diffNow();

        if (timeDiff.milliseconds < 0) {
            return true;
        }

        return false;
    }

    compare(a: string, b: string): number {
        if (a === b) return 0;
        return this.isBefore(a, b) ? -1 : 1;
    }

    isTodayOrAfter(dateTime: string): boolean {
        if (!dateTime) {
            return false;
        }

        const timeDiff = this.parseDateTime(dateTime).diffNow();

        if (timeDiff.milliseconds >= 0) {
            return true;
        }

        return false;
    }

    getCurrentMonth() {
        return luxon.DateTime.local().month;
    }

    getCurrentYear() {
        return luxon.DateTime.local().year;
    }

    getCurrentDateTime(): string {
        return luxon.DateTime.utc().toISO();
    }
    getCurrentDateTimeLocal(): string {
        return luxon.DateTime.local().toISO();
    }

    convertDateTimeToJson(dateTime: string): string {
        // If already in ISO format, return
        if (dateTime?.indexOf('T') > -1) {
            return dateTime;
        }

        const opt: luxon.DateTimeOptions = {};

        opt.setZone = true;
        opt.zone = 'GMT';

        const returnDateTime = DateTime.fromFormat(dateTime, this.DATE_TIME_FORMAT, opt).toJSON();

        return returnDateTime;
    }

    formatDateTimeToLocal(dateTime: string, format?: string): string {

        if (!dateTime) {
            return '';
        }

        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return this.formatDateTimeToTimezone(dateTime, browserTimeZone, format);
    }

    convertDateTimeToUtc(dateTime: string): string {
        const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const opt: luxon.DateTimeOptions = {};

        opt.setZone = true;
        opt.zone = browserTimeZone;

        return DateTime.fromISO(dateTime, opt).setZone('UTC').toISO();
    }

    formatDuration(timeInSeconds: number) {
        const isNegative = (timeInSeconds < 0);
        if (isNegative) {
            timeInSeconds = -timeInSeconds;
        }

        let totalSeconds = timeInSeconds;

        const seconds = (totalSeconds % 60);

        let totalMinutes = (timeInSeconds - seconds) / 60;

        const minutes = totalMinutes % this.minutesConst;

        let totalHours = (totalMinutes - minutes) / 60;

        const hours = totalHours % 24;

        let days = (totalHours - hours) / 24;

        let prefix = '';

        if (days == 1) {
            prefix = '1 day ';
        }
        if (days > 1) {
            prefix = `${days.toFixed(0)} days `;
        }

        if (isNegative) {
            prefix = '-' + prefix;
        }

        if (hours > 0) {
            return `${prefix}${this.pad(hours, 2)}:${this.pad(minutes, 2)}:${this.pad(seconds, 2)}`;
        }

        return `${prefix}${this.pad(minutes, 2)}:${this.pad(seconds, 2)}`;
    }

    private pad(num: number, size = 0, decimals = 0) {
        var numString = num.toFixed(0).toString();
        while (numString.length < size) numString = "0" + numString;
        return numString;
    }


    formatDuration2(timeInSeconds: number) {
        const isNegative = (timeInSeconds < 0);
        if (isNegative) {
            timeInSeconds = -timeInSeconds;
        }

        let totalSeconds = timeInSeconds;

        const seconds = (totalSeconds % 60);

        let totalMinutes = (timeInSeconds - seconds) / 60;

        const minutes = totalMinutes % this.minutesConst;

        let totalHours = (totalMinutes - minutes) / 60;

        const hours = totalHours % 24;

        let days = (totalHours - hours) / 24;

        let prefix = '';

        if (days == 1) {
            prefix = '1 day ';
        }
        if (days > 1) {
            prefix = `${days.toFixed(0)} days `;
        }

        if (isNegative) {
            prefix = '-' + prefix;
        }

        let result = prefix;

        if (hours == 1) {
            result += `${this.pad(hours, 1)} hour `;
        }
        if (hours > 1) {
            result += `${this.pad(hours, 1)} hours `;
        }

        if (minutes > 0) {
            result += `${this.pad(minutes, 2)} minutes`;
        }

        return result;
    }

    formatDurationMinutesSeconds(durationSeconds: number, options?: { showSeconds?: boolean, showText?: boolean }) {
        if (!durationSeconds) {
            return '';
        }

        let seconds = durationSeconds % 60;
        const totalMinutes = ((durationSeconds - seconds) / 60);
        const minutes = totalMinutes % 60;
        const hours = (totalMinutes - minutes) / 60;

        seconds = Math.round(seconds);

        if (options?.showText) {
            const parts: string[] = [];

            if (hours > 0) {
                parts.push(`${hours} hours`);
            }

            if (minutes > 0 || (options?.showSeconds && seconds > 0)) {
                parts.push(`${minutes} minutes`);
            }

            if (options?.showSeconds) {
                parts.push(`${seconds} seconds`);
            }

            if (parts.length > 0) {
                return parts.join(' ');
            }

            return '';
        }

        let minutesSeconds = `${minutes}:${this.pad(seconds, 2)}`;

        if (minutes < 10 && hours > 0) {
            minutesSeconds = `${this.pad(minutes, 2)}:${this.pad(seconds, 2)}`;
        }

        if (hours > 0) {
            return `${hours}:${minutesSeconds}`;
        }

        return `${minutesSeconds}`;
    }
}
